import {
  AssociatedFileStatus,
  ControlType,
  CustomFieldDefinition,
  CustomFieldUsage,
  EmailTemplateType,
  FieldDataType,
  FieldStatus,
  FormatValidation,
} from '@alucio/aws-beacon-amplify/src/models'
import {
  BODY_DEFAULT_VALUE,
  BODY_ERRORS,
  BODY_PLACEHOLDERS,
} from 'src/components/EmailTemplateDrawer/EmailDrawerHelper'
import { CustomFieldValuesMap, EmailTemplateORM } from 'src/types/orms'
import * as z from 'zod'
import { CustomFieldValues } from '../useUserEditFormConfig/UserEditConfig'
import DOMPurify from 'dompurify';

const templateCreatedAt =  new Date().toUTCString()

export const fields: CustomFieldDefinition[] = [
  {
    id: 'title',
    fieldLabel: 'Template Name',
    fieldName: 'Title',
    status: FieldStatus.ENABLED,
    required: true,
    fieldType: FieldDataType.STRING,
    usage: [CustomFieldUsage.EMAIL_TEMPLATE_FILTER],
    controlType: ControlType.INPUT,
    fieldValueDefinitions: [],
    createdAt: templateCreatedAt,
  },
  {
    id: 'subject',
    fieldLabel: 'Subject',
    fieldName: 'Subject',
    status: FieldStatus.ENABLED,
    required: true,
    fieldType: FieldDataType.STRING,
    usage: [CustomFieldUsage.EMAIL_TEMPLATE_FILTER],
    controlType: ControlType.INPUT,
    fieldValueDefinitions: [],
    createdAt: templateCreatedAt,
  },
  {
    id: 'cc',
    fieldLabel: 'cc',
    fieldName: 'cc',
    status: FieldStatus.ENABLED,
    required: false,
    fieldType: FieldDataType.STRING,
    usage: [CustomFieldUsage.EMAIL_TEMPLATE_FILTER],
    controlType: ControlType.INPUT_CSV,
    formatValidation: FormatValidation.EMAIL_CSV,
    fieldValueDefinitions: [],
    createdAt: templateCreatedAt,
  },
  {
    id: 'bcc',
    fieldLabel: 'bcc',
    fieldName: 'bcc',
    status: FieldStatus.ENABLED,
    required: false,
    fieldType: FieldDataType.STRING,
    usage: [CustomFieldUsage.EMAIL_TEMPLATE_FILTER],
    controlType: ControlType.INPUT_CSV,
    formatValidation: FormatValidation.EMAIL_CSV,
    fieldValueDefinitions: [],
    createdAt: templateCreatedAt,
  },
  {
    id: 'body',
    fieldLabel: 'Body',
    fieldName: 'Body',
    status: FieldStatus.ENABLED,
    required: true,
    fieldType: FieldDataType.STRING,
    usage: [CustomFieldUsage.EMAIL_TEMPLATE_FILTER],
    controlType: ControlType.TEXTAREA,
    fieldValueDefinitions: [],
    createdAt: templateCreatedAt,
  },
  {
    id: 'attachments',
    fieldLabel: 'attachments',
    fieldName: 'attachments',
    status: FieldStatus.ENABLED,
    required: false,
    fieldType: FieldDataType.STRING,
    usage: [CustomFieldUsage.EMAIL_TEMPLATE_FILTER],
    controlType: ControlType.INPUT,
    fieldValueDefinitions: [],
    createdAt: templateCreatedAt,
  },
  {
    id: 'type',
    fieldLabel: 'Type',
    fieldName: 'Type',
    status: FieldStatus.ENABLED,
    required: true,
    fieldType: FieldDataType.CATEGORICAL,
    usage: [CustomFieldUsage.EMAIL_TEMPLATE_FILTER],
    controlType: ControlType.SELECT,
    fieldValueDefinitions: [{
      id: 'PLAIN_TEXT',
      value: EmailTemplateType.PLAIN_TEXT,
      label: 'Plain Text',
      disabled: false,
      createdAt: templateCreatedAt,
      dependentValueIds: [],
    },
    {
      id: 'HTML',
      value: EmailTemplateType.HTML,
      label: 'HTML',
      disabled: false,
      createdAt: templateCreatedAt,
      dependentValueIds: [],
    }],
    createdAt: templateCreatedAt,
  },
]

const emailTemplateBodyValidation = (value, ctx: z.RefinementCtx) => {
  if (value.body) {
    if (value.type === EmailTemplateType.HTML) {
      const sanitizedBody = DOMPurify.sanitize(value.body as unknown as string);
      value.body = sanitizedBody;
    }
    const hasAttachments = value.attachments === 'true'
    const errors: string[] = []
    for (const placeholder in BODY_PLACEHOLDERS) {
      const replaceRegex = new RegExp(/\[placeholder\]/, 'ig')
      const firstIndex = value.body.indexOf(BODY_PLACEHOLDERS[placeholder])
      const mustExist = placeholder === 'files' || (placeholder === 'attachments' && hasAttachments)

      if (mustExist && firstIndex === -1) {
        // NO PRESENCE OF PLACEHOLDER
        errors.push(BODY_ERRORS.noInstances.replaceAll(replaceRegex, BODY_PLACEHOLDERS[placeholder]))
        continue
      }
      // MORE THAN ONCE
      if (firstIndex !== -1) {
        const secondIndex = value.body.substring(firstIndex + 1).indexOf(BODY_PLACEHOLDERS[placeholder])
        if (secondIndex !== -1 && placeholder !== 'linkUrl') {
          errors.push(BODY_ERRORS.manyInstances.replaceAll(replaceRegex, BODY_PLACEHOLDERS[placeholder]))
        }
      }
    }

    // additional validation on top of the transform
    if (errors.length > 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: errors.join('\n'),
        path: ['body'],
      })
    }
  }
}

// inside of overrideSchema see which field is being validated and apply the specific one here. example if its cc or bcc then validate email commas sperated
const overrideSchema = {
  superRefine: [emailTemplateBodyValidation],
}

export const emailFormConfig = {
  fields,
  overrideSchema,
}

export const getEmailTemplateDefaultValues = (emailORM?: EmailTemplateORM): CustomFieldValuesMap => {
  const title = emailORM?.model.title || ''
  const subject = emailORM?.model.subject || ''
  const cc = emailORM?.model.cc || []
  const bcc = emailORM?.model.bcc || []
  const type = emailORM?.model.type || EmailTemplateType.PLAIN_TEXT
  const body = emailORM?.model.body || BODY_DEFAULT_VALUE
  // The value is string true or false because it is a workaround for CustomFieldValuesMap not accepting boolean as CustomFieldValue
  const hasActiveAttachments = emailORM?.relations
    .associatedFiles?.some(associatedFile => associatedFile.model.status !== AssociatedFileStatus.DELETED)
  const attachments = hasActiveAttachments ? 'true' : 'false'

  return {
    title: {
      field: fields[0],
      values: [title],
      displayValues: [title],
    },
    subject: {
      field: fields[1],
      values: [subject],
      displayValues: [subject],
    },
    cc: {
      field: fields[2],
      values: cc,
      displayValues: cc,
    },
    bcc: {
      field: fields[3],
      values: bcc,
      displayValues: bcc,
    },
    body: {
      field: fields[4],
      values: [body],
      displayValues: [body],
    },
    attachments: {
      field: fields[5],
      values: [attachments],
      displayValues: [],
    },
    type: {
      field: fields[6],
      values: [type],
      displayValues: [type],
    },
  }
}

export const initialEmailTemplateValues =
  (emailTemplateORM?: EmailTemplateORM, customFields?: CustomFieldDefinition[]) => {
    const emailTemplateLabels = customFields?.reduce<Record<string, CustomFieldValues>>((acc, field) => {
      acc[field.id] = {
        field: field,
        values: emailTemplateORM?.meta.customFilterValues[field.id]?.valuesDefinition?.map(vd => vd.id) || [],
        displayValues: emailTemplateORM?.meta.customFilterValues[field.id]?.displayValues || [],
      }
      return acc;
    }, {});

    const defaultValues = getEmailTemplateDefaultValues(emailTemplateORM)

    return {
      ...defaultValues,
      ...emailTemplateLabels,
    };
  }
